import { useCallback } from 'react';
import { buildTabPath } from './utils';

/**
 * useTabHistory Hook
 * 
 * Manages per-tab navigation history (like browser tab history).
 * Each tab has its own history stack that allows back/forward navigation.
 * 
 * History is SESSION-ONLY - it resets on page refresh.
 */
export function useTabHistory({ tabs, activeTabId, setTabs, saveTabs }) {
  /**
   * Check if the active tab can go back in its history
   */
  const canGoBack = useCallback(() => {
    const tab = tabs.find(t => t.id === activeTabId);
    if (!tab || !tab.history) return false;
    const index = tab.historyIndex ?? (tab.history.length - 1);
    return index > 0;
  }, [tabs, activeTabId]);

  /**
   * Check if the active tab can go forward in its history
   */
  const canGoForward = useCallback(() => {
    const tab = tabs.find(t => t.id === activeTabId);
    if (!tab || !tab.history) return false;
    const index = tab.historyIndex ?? (tab.history.length - 1);
    return index < tab.history.length - 1;
  }, [tabs, activeTabId]);

  /**
   * Navigate back in the active tab's history
   * @returns {Object|null} Object with { entry, previousState } or null if can't go back
   */
  const goBack = useCallback(() => {
    const tab = tabs.find(t => t.id === activeTabId);
    if (!tab || !tab.history) return null;
    
    const currentIndex = tab.historyIndex ?? (tab.history.length - 1);
    if (currentIndex <= 0) return null;
    
    const newIndex = currentIndex - 1;
    const historyEntry = tab.history[newIndex];
    
    // Capture previous state before updating
    const previousState = {
      type: tab.type,
      targetId: tab.targetId,
      viewMode: tab.viewMode,
      customViewId: tab.customViewId,
    };
    
    // Update tab to reflect the history entry
    setTabs(prevTabs => {
      const newTabs = prevTabs.map(t => {
        if (t.id !== activeTabId) return t;
        return {
          ...t,
          type: historyEntry.type,
          targetId: historyEntry.targetId,
          viewMode: historyEntry.viewMode,
          customViewId: historyEntry.customViewId,
          label: historyEntry.label,
          icon: historyEntry.icon,
          path: buildTabPath(historyEntry.type, historyEntry.targetId, historyEntry.viewMode, historyEntry.customViewId),
          historyIndex: newIndex,
        };
      });
      saveTabs(newTabs, activeTabId);
      return newTabs;
    });
    
    return { entry: historyEntry, previousState };
  }, [tabs, activeTabId, setTabs, saveTabs]);

  /**
   * Navigate forward in the active tab's history
   * @returns {Object|null} Object with { entry, previousState } or null if can't go forward
   */
  const goForward = useCallback(() => {
    const tab = tabs.find(t => t.id === activeTabId);
    if (!tab || !tab.history) return null;
    
    const currentIndex = tab.historyIndex ?? (tab.history.length - 1);
    if (currentIndex >= tab.history.length - 1) return null;
    
    const newIndex = currentIndex + 1;
    const historyEntry = tab.history[newIndex];
    
    // Capture previous state before updating
    const previousState = {
      type: tab.type,
      targetId: tab.targetId,
      viewMode: tab.viewMode,
      customViewId: tab.customViewId,
    };
    
    // Update tab to reflect the history entry
    setTabs(prevTabs => {
      const newTabs = prevTabs.map(t => {
        if (t.id !== activeTabId) return t;
        return {
          ...t,
          type: historyEntry.type,
          targetId: historyEntry.targetId,
          viewMode: historyEntry.viewMode,
          customViewId: historyEntry.customViewId,
          label: historyEntry.label,
          icon: historyEntry.icon,
          path: buildTabPath(historyEntry.type, historyEntry.targetId, historyEntry.viewMode, historyEntry.customViewId),
          historyIndex: newIndex,
        };
      });
      saveTabs(newTabs, activeTabId);
      return newTabs;
    });
    
    return { entry: historyEntry, previousState };
  }, [tabs, activeTabId, setTabs, saveTabs]);

  return {
    canGoBack,
    canGoForward,
    goBack,
    goForward,
  };
}

export default useTabHistory;
